export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

type Options = Omit<RequestInit, 'body'> & { body?: unknown };

/**
 * Restaurante da tela atual (definido por TenantApp a partir de /r/{slug} ou /painel/{slug}).
 * As chamadas `/api/...` das telas de restaurante vão para `/api/t/{slug}/...`; o painel DEV e a landing
 * continuam em `/api/...`. O slug só identifica o restaurante: quem autoriza é o servidor.
 */
let tenantSlug: string | null = null;
export function setTenantSlug(slug: string | null) {
  tenantSlug = slug;
}
export function getTenantSlug() {
  return tenantSlug;
}

/** Caminho final de uma rota da API (use também em links de download e EventSource). */
export function apiUrl(path: string) {
  if (!tenantSlug || !path.startsWith('/api/') || path.startsWith('/api/t/') || path.startsWith('/api/dev/')) return path;
  return `/api/t/${encodeURIComponent(tenantSlug)}${path.slice('/api'.length)}`;
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const isForm = opts.body instanceof FormData;
  const res = await fetch(apiUrl(path), {
    // Dados ao vivo: nunca usar resposta guardada pelo navegador (o servidor também manda Cache-Control: no-store).
    cache: 'no-store',
    ...opts,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(opts.body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
      ...opts.headers,
    },
    body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; message?: string; fields?: Record<string, string> };
    throw new ApiError(res.status, d.error ?? 'error', d.message ?? 'Algo deu errado. Tente novamente.', d.fields ?? {});
  }
  return data as T;
}

function safeJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const post = <T = unknown>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} });
export const patch = <T = unknown>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const put = <T = unknown>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body });

export function errorMessage(err: unknown) {
  return err instanceof ApiError ? err.message : 'Falha de conexão. Verifique sua internet.';
}
export function fieldErrors(err: unknown): Record<string, string> {
  return err instanceof ApiError ? err.fields : {};
}
